<script setup lang="ts">
import { toRef } from 'vue';

import VLoader from '@components/loader/VLoader.vue';
import VFormPlainDataListbox from '@modules/forms/components/inputs/VFormPlainDataListbox/VFormPlainDataListbox.vue';
import VReportUploadFile from '@modules/reports/components/VReportUploadFile/VReportUploadFile.vue';
import VSmartFilter from '@modules/smart-filters/components/VSmartFilter/VSmartFilter.vue';
import { useUserSupervision } from '@modules/user/composables';
import { isArray } from '@utils/guards';

import type { IReportTabContentEmits, IReportTabContentProps } from './types';
import { useReportTabContent } from './composables';

const props = defineProps<IReportTabContentProps>();
const emit = defineEmits<IReportTabContentEmits>();

const { isSupervision } = useUserSupervision();

const {
  selectedFilterColumnId,
  innerTabData,
  selectedStatsColumnData,
  statsColumns,
  innerTabFetching,
  isChangeInnerTabLoading,
  isStatsColumnChangeLoading,
  isDialogOpened,
  onSelectStatsColumn,
  onChangeStatsData,
  onInnerTabDataChange,
  toggleDialog,
} = useReportTabContent(
  toRef(props, 'activeTab'),
  toRef(props, 'activeInnerTab'),
  toRef(props, 'queryOptions'),
  toRef(props, 'isSegmentsType'),
  toRef(props, 'translationKey'),
  emit,
);
</script>

<template>
  <VLoader v-data-qa-auto-id="'VReportTabContent'"
    :loading="innerTabFetching"
    mode="structural-immediate"
    min-height="348px"
    class="border-b border-gray-200"
  >
    <div>
      <VSmartFilter
        v-if="activeInnerTab.advancedFilter"
        :data="selectedStatsColumnData"
        :selected-column-id="selectedFilterColumnId"
        :columns="statsColumns"
        :is-saving-column="isStatsColumnChangeLoading"
        :is-supervision="isSupervision"
        @data-change="onChangeStatsData"
        @column-change="onSelectStatsColumn"
      />
      <VFormPlainDataListbox
        v-else-if="!activeInnerTab?.advancedFilter && isArray(innerTabData)"
        :data="innerTabData"
        :disabled="activeTab.read_only || isChangeInnerTabLoading || isSupervision"
        :upload-file-btn="activeInnerTab.uploadFile"
        :show-info-message="activeTab.read_only"
        :translation-path="translationPathForList ? `${translationPathForList}.` : undefined"
        :info-message="isSegmentsType
          ? undefined
          : $getTranslation(translationKey, 'REPORTS.STRUCTURES_READONLY_MESSAGE', { name: activeTab.name || activeTab.id })
        "
        sort-after-change-item
        hide-errors
        no-border
        is-full-width
        @change="onInnerTabDataChange"
        @upload-file="() => toggleDialog(true)"
      />
    </div>
  </VLoader>

  <VReportUploadFile
    :active-tab="activeTab"
    :active-inner-tab="activeInnerTab"
    :query-options="queryOptions"
    :visible="isDialogOpened"
    @toggle-dialog="toggleDialog"
  />
</template>
